/**
 * Lean 3D gradient noise and fbm for the sky bake and the gas giant, emitted ONCE as WGSL/GLSL functions
 * (`setLayout`) so every call site is a plain function call. The MaterialX noise in three inlines its fractal loop
 * at each call site, which cost about 100 ms of cold pipeline compile per call on the RX 7800 XT; with ten call
 * sites the sky bake blew its 300 ms budget on compile alone. Semantics follow `mx_fractal_noise_float`: an
 * unnormalized sum of octaves (amplitude 1, then × gain), lacunarity 2, output of one octave roughly in [-1, 1].
 */
import type { Node } from 'three/webgpu'
import { Fn, Loop, dot, float, floor, fract, int, mat3, mix, vec3 } from 'three/tsl'

type F = Node<'float'>
type V3 = Node<'vec3'>

/** Dave Hoskins' hash33 (no sine), stable for lattice coordinates up to a few thousand. */
const hash33 = Fn(([p0]: [V3]) => {
  const p = fract(p0.mul(vec3(0.1031, 0.103, 0.0973))).toVar()
  p.addAssign(dot(p, p.yxz.add(33.33)))
  return fract(p.xxy.add(p.yxx).mul(p.zyx))
}).setLayout({ name: 'eclHash33', type: 'vec3', inputs: [{ name: 'p', type: 'vec3' }] })

/** Quintic-interpolated gradient noise, roughly [-1, 1]. */
const noise3Fn = Fn(([x]: [V3]) => {
  const i = floor(x).toVar()
  const f = fract(x).toVar()
  const u = f.mul(f).mul(f).mul(f.mul(f.mul(6).sub(15)).add(10)).toVar()
  const corner = (ox: number, oy: number, oz: number): F => {
    const o = vec3(ox, oy, oz)
    return dot(hash33(i.add(o)).mul(2).sub(1), f.sub(o))
  }
  const x00 = mix(corner(0, 0, 0), corner(1, 0, 0), u.x)
  const x10 = mix(corner(0, 1, 0), corner(1, 1, 0), u.x)
  const x01 = mix(corner(0, 0, 1), corner(1, 0, 1), u.x)
  const x11 = mix(corner(0, 1, 1), corner(1, 1, 1), u.x)
  return mix(mix(x00, x10, u.y), mix(x01, x11, u.y), u.z).mul(1.9)
}).setLayout({ name: 'eclNoise3', type: 'float', inputs: [{ name: 'x', type: 'vec3' }] })

/** Three decorrelated gradient-noise channels from the same lattice hashes (one hash per corner, not three). */
const noise3vFn = Fn(([x]: [V3]) => {
  const i = floor(x).toVar()
  const f = fract(x).toVar()
  const u = f.mul(f).mul(f).mul(f.mul(f.mul(6).sub(15)).add(10)).toVar()
  const corner = (ox: number, oy: number, oz: number): V3 => {
    const o = vec3(ox, oy, oz)
    const g = hash33(i.add(o)).mul(2).sub(1)
    const d = f.sub(o)
    return vec3(dot(g, d), dot(g.yzx, d.zxy), dot(g.zxy, d.yzx.mul(vec3(1, -1, 1))))
  }
  const x00 = mix(corner(0, 0, 0), corner(1, 0, 0), u.x)
  const x10 = mix(corner(0, 1, 0), corner(1, 1, 0), u.x)
  const x01 = mix(corner(0, 0, 1), corner(1, 0, 1), u.x)
  const x11 = mix(corner(0, 1, 1), corner(1, 1, 1), u.x)
  return mix(mix(x00, x10, u.y), mix(x01, x11, u.y), u.z).mul(1.9)
}).setLayout({ name: 'eclNoise3v', type: 'vec3', inputs: [{ name: 'x', type: 'vec3' }] })

/** A fixed rotation between octaves (with lacunarity 2) so lattice axes never line up. */
const OCTAVE = mat3(0.0, 1.6, 1.2, -1.6, 0.72, -0.96, -1.2, -0.96, 1.28)

const fbmFn = Fn(([p0, octaves, gain]: [V3, Node<'int'>, F]) => {
  const p = vec3(p0).toVar()
  const sum = float(0).toVar()
  const amp = float(1).toVar()
  Loop({ start: int(0), end: octaves, type: 'int', condition: '<' }, () => {
    sum.addAssign(noise3Fn(p).mul(amp))
    p.assign(OCTAVE.mul(p))
    amp.mulAssign(gain)
  })
  return sum
}).setLayout({
  name: 'eclFbm3',
  type: 'float',
  inputs: [
    { name: 'p', type: 'vec3' },
    { name: 'octaves', type: 'int' },
    { name: 'gain', type: 'float' },
  ],
})

const fbmVecFn = Fn(([p0, octaves, gain]: [V3, Node<'int'>, F]) => {
  const p = vec3(p0).toVar()
  const sum = vec3(0, 0, 0).toVar()
  const amp = float(1).toVar()
  Loop({ start: int(0), end: octaves, type: 'int', condition: '<' }, () => {
    sum.addAssign(noise3vFn(p).mul(amp))
    p.assign(OCTAVE.mul(p))
    amp.mulAssign(gain)
  })
  return sum
}).setLayout({
  name: 'eclFbm3v',
  type: 'vec3',
  inputs: [
    { name: 'p', type: 'vec3' },
    { name: 'octaves', type: 'int' },
    { name: 'gain', type: 'float' },
  ],
})

/** One octave of gradient noise, roughly [-1, 1]. */
export function noise3(p: V3): F {
  return noise3Fn(p)
}

/** Fractal sum of `octaves` gradient-noise octaves (lacunarity 2, amplitude × `gain` per octave). */
export function fbm3(p: V3, octaves: number, gain = 0.5): F {
  return fbmFn(p, int(octaves), float(gain))
}

/**
 * Three decorrelated fbm channels in ONE call (domain warping, or three layers at the same frequency). Costs about
 * the same compile time as one fbm3 call; use it instead of several fbm3 calls wherever the frequencies can match.
 */
export function fbmVec3(p: V3, octaves: number, gain = 0.5): V3 {
  return fbmVecFn(p, int(octaves), float(gain))
}
